import { AsyncPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { AssetsService } from '@sc/data-access-assets';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  Appointment,
  CLIENT_APP_ID,
  ClientAppId,
  Customer,
  Fitter,
  LocationData,
  LocationDataGeocoded,
  emailPattern,
  getMatchingPhonePattern,
  getOperationType,
  phonePatterns,
  AppointmentSheetValidationData,
  ErrorMessage,
  HTMLElementEvent,
} from '@sc/data-access-models';
import {
  DossierSandboxService,
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import {
  FITTER_NAME_DISPLAY_CONTEXT,
  FitterNameDisplayContext,
  FitterNameDisplayService,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  AppointmentUtils,
  getTranslatableDuration,
  resolveInterventionDuration,
} from '@sc/utils';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';
import { MomentParseZonePipe } from '../../pipes';
import { ErrorMessagesService } from '../../services';
import { CustomValidators } from '../../validators';
import { AsteriskInfoComponent } from '../asterisk-info';
import { ButtonComponent } from '../button';
import { ErrorComponent } from '../error';
import { IconComponent } from '../icon';
import { InputTextComponent } from '../input-text';
import { PhoneInputComponent } from '../phone';

@Component({
  selector: 'sc-appointment-selection-sheet',
  styleUrls: ['./appointment-selection-sheet.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FitterNameDisplayService,
    {
      provide: FITTER_NAME_DISPLAY_CONTEXT,
      useValue: FitterNameDisplayContext.DialogSelectApmt,
    },
  ],
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
    AsteriskInfoComponent,
    MomentParseZonePipe,
    InputTextComponent,
    PhoneInputComponent,
    ButtonComponent,
    ErrorComponent,
    PhoneNumberPipe,
    FitterNamePipe,
    LocaleDatePipe,
    AsyncPipe,
    MatRadioModule,
    TranslateModule,
  ],
  template: `
    <div class="container" [class.overlay-customer]="isCustomerClientAppId()">
      <button type="button" class="close-btn" (click)="onCancelAppointment()">
        <sc-icon name="close"></sc-icon>
        <span class="sr_only">{{
          'GlobalApplicativeConstants.accessibility.closeDialog'
            | translate
              : {
                  dialogTitle:
                    'CustomerDeclaration.ConfirmAppointmentSheet.dialogTitle'
                    | translate
                }
        }}</span>
      </button>

      <h1 class="sr_only" id="appointment-selection-title-id" mat-dialog-title>
        {{
          'CustomerDeclaration.ConfirmAppointmentSheet.dialogTitle' | translate
        }}
      </h1>

      <div class="scrollable-container">
        <div class="big-icon-container">
          <span
            [class]="'icon-back-circle ' + getTypeAppointmentClass()"
            [class.with-fitter-logo]="displayFitterLogo"
          >
            <ng-container
              *ngIf="displayFitterLogo; else defaultAppointmentIcon"
            >
              <img
                class="appointment-selection-sheet-logo"
                [src]="getDisplayedFitterLogoPath()"
                (error)="onFitterLogoError()"
                [alt]="
                  (fitterLogoAlt$ | async) ||
                  configurationService.getBrandName(fitter?.brandId)
                "
              />
            </ng-container>
            <ng-template #defaultAppointmentIcon>
              <sc-icon
                name="book_appointment"
                specificName="appointment_selection_sheet_book"
              ></sc-icon>
            </ng-template>
          </span>
        </div>

        <sc-asterisk-info
          *ngIf="
            isAppointmentRequest() ||
            showAddressCompletionForm() ||
            showLocationChoice() ||
            showReplacementVehicleChoice() ||
            displayCustomerContactConfirmation()
          "
        ></sc-asterisk-info>

        <h2
          class="sc-design-title-h2"
          *ngIf="showAddressCompletionForm() && showUnknownAddressForm"
        >
          {{
            'CustomerDeclaration.ConfirmAppointmentSheet.messageAddressNotFound'
              | translate
          }}
        </h2>

        <div
          *ngIf="!isAppointmentRequest()"
          class="info-selected-appointment-container"
        >
          <div
            role="heading"
            [attr.aria-level]="
              appointment?.outsideIntervention && showUnknownAddressForm ? 3 : 2
            "
            class="sc-design-text-2 subtitle"
          >
            {{
              'CustomerDeclaration.ConfirmAppointmentSheet.headerSelectedAppointment'
                | translate
            }}
          </div>

          <div class="info-selected-appointment">
            <div
              [class]="
                'sc-design-text-4-bold appointment-info-box info-box info-box-where ' +
                getTypeAppointmentClass()
              "
            >
              <sc-icon
                [name]="appointment?.outsideIntervention ? 'home' : 'fitter'"
                [specificName]="
                  appointment?.outsideIntervention
                    ? 'search_home'
                    : 'search_fitter'
                "
              ></sc-icon>
              <p>
                {{
                  (appointment?.outsideIntervention
                    ? 'CustomerDeclaration.ConfirmAppointmentSheet.atHome'
                    : 'CustomerDeclaration.ConfirmAppointmentSheet.inCenter'
                  ) | translate
                }}
              </p>
            </div>

            <div
              [class]="
                'sc-design-text-4-bold appointment-info-box info-box info-box-date ' +
                getTypeAppointmentClass()
              "
            >
              <p>
                {{
                  appointment?.time
                    | parseZoneTimeString
                      : timezoneService.getTimezone(appointment)
                    | localeDate: 'fullDate'
                    | async
                }}
              </p>
            </div>

            <div
              [class]="
                'sc-design-text-4-bold appointment-info-box info-box info-box-time ' +
                getTypeAppointmentClass()
              "
            >
              <p>{{ dossierSB.getTime(appointment) }}</p>
            </div>
          </div>
        </div>
        <div *ngIf="isOutsideIntervention()" class="address-container">
          <h2 class="sc-design-text-2  subtitle">
            {{
              'CustomerDeclaration.ConfirmAppointmentSheet.headerInterventionAddress'
                | translate
            }}
          </h2>

          <div
            *ngIf="!showAddressCompletionForm()"
            [class]="
              'sc-design-text-4-bold info-box ' + getTypeAppointmentClass()
            "
          >
            {{ location.address }}
          </div>

          <div *ngIf="showAddressCompletionForm() && !showUnknownAddressForm">
            <sc-input-text
              type="text"
              autocomplete="street-address"
              [label]="
                'CustomerDeclaration.ConfirmAppointmentSheet.streetName'
                  | translate
              "
              suggestionsLabelName="address"
              [dataTestId]="'nomDeVoie'"
              [value]="selectedAddressStr"
              [hint]="getAutocompleteHintText()"
              [suggestions]="googleAutoCompleteResponse"
              [forceDisplayError]="isAddressInputError"
              [forceHighlightRequired]="false"
              (input)="onAddressChangeValue($event)"
              (updateSuggestions)="onUpdateAddressSuggestions($event)"
              (selectSuggestion)="onSelectAddress($event)"
            ></sc-input-text>
          </div>

          <div *ngIf="showAddressCompletionForm() && showUnknownAddressForm">
            <div class="street-complementary-infos">
              <div class="left-side">
                <sc-input-text
                  type="text"
                  autocomplete="address-line1"
                  [label]="
                    'CustomerDeclaration.ConfirmAppointmentSheet.streetNum'
                      | translate
                  "
                  [value]="modifiedLocation?.addressElements.streetNumber"
                  (input)="onChangeStreetNumber($event)"
                ></sc-input-text>
              </div>
              <div class="right-side">
                <sc-input-text
                  type="text"
                  autocomplete="address-line2"
                  [label]="
                    'CustomerDeclaration.ConfirmAppointmentSheet.streetType'
                      | translate
                  "
                  [value]="modifiedLocation?.addressElements.streetSuffix"
                  (input)="onChangeStreetSuffix($event)"
                ></sc-input-text>
              </div>
            </div>
            <div>
              <sc-input-text
                type="text"
                autocomplete="address-line3"
                [label]="
                  'CustomerDeclaration.ConfirmAppointmentSheet.streetName'
                    | translate
                "
                [value]="modifiedLocation?.addressElements.streetName"
                [forceHighlightRequired]="false"
                (input)="onChangeStreetName($event)"
              ></sc-input-text>
            </div>
            <div class="street-complementary-infos">
              <div class="left-side">
                <sc-input-text
                  type="text"
                  autocomplete="postal-code"
                  [label]="
                    'CustomerDeclaration.ConfirmAppointmentSheet.postalCode'
                      | translate
                  "
                  [disabled]="
                    !featureFlipManager.isFeatureActivated(
                      'unlock-city-when-address-not-found'
                    ) && !!location?.addressElements?.postalCode
                  "
                  [forceHighlightRequired]="false"
                  [value]="modifiedLocation?.addressElements.postalCode"
                  (input)="onChangePostalCode($event)"
                ></sc-input-text>
              </div>
              <div class="right-side">
                <sc-input-text
                  type="text"
                  autocomplete="address-level2"
                  [label]="
                    'CustomerDeclaration.ConfirmAppointmentSheet.cityLabel'
                      | translate
                  "
                  [disabled]="
                    !featureFlipManager.isFeatureActivated(
                      'unlock-city-when-address-not-found'
                    ) && !!location?.addressElements?.city
                  "
                  [forceHighlightRequired]="false"
                  [value]="modifiedLocation?.addressElements.city"
                  (input)="onChangeCity($event)"
                ></sc-input-text>
              </div>
            </div>
          </div>
        </div>

        <div class="address-container">
          <div
            class="sc-design-text-2  subtitle"
            role="heading"
            [attr.aria-level]="showUnknownAddressForm ? 3 : 2"
            tabindex="-1"
          >
            {{
              (isOutsideIntervention()
                ? 'CustomerDeclaration.ConfirmAppointmentSheet.headerFitterInfos'
                : isAppointmentRequest()
                ? 'CustomerDeclaration.ConfirmAppointmentSheet.requestAppointment.headerInterventionAddress'
                : 'CustomerDeclaration.ConfirmAppointmentSheet.headerInterventionAddress'
              ) | translate
            }}
          </div>

          <div
            [class]="
              'sc-design-text-4-bold info-box center-infos multi-infos-box ' +
              getTypeAppointmentClass()
            "
          >
            <p
              role="heading"
              [attr.aria-level]="showUnknownAddressForm ? 4 : 3"
              tabindex="-1"
            >
              {{ fitter | fitterName | async }}
            </p>
            <p
              *ngIf="fittersSB.isNotItinerant(fitter)"
              [innerHtml]="fittersSB.getCompleteAddress(fitter)"
            ></p>
            <p class="sc-design-link-bold sc-conf-display-fitter-phone">
              <span class="phone-infos">
                <sc-icon name="phone"></sc-icon>
                <span>
                  {{ fitter.phone | phoneNumber | async }}
                </span>
              </span>
            </p>
          </div>
          <div
            *ngIf="isAppointmentRequest()"
            class="description sc-design-text-3"
          >
            {{ getRequestAppointmentDescription() | translate }}
          </div>
        </div>

        <div *ngIf="showLocationChoice()" class="location-choice">
          <h2 id="locationChoiceQuestion" class="sc-design-text-2  subtitle">
            {{
              'CustomerDeclaration.ConfirmAppointmentSheet.locationChoiceQuestion'
                | translate
            }}
          </h2>
          <mat-radio-group
            [(ngModel)]="locationChoice"
            [attr.aria-describedby]="
              'locationChoiceQuestion' +
              (isErrorLocationChoice() ? ' errorLocation' : '')
            "
          >
            <mat-radio-button value="center">
              <span class="sc-design-text-3">{{
                'CustomerDeclaration.ConfirmAppointmentSheet.inCenter'
                  | translate
              }}</span>
            </mat-radio-button>
            <mat-radio-button value="outside">
              <span class="sc-design-text-3">{{
                'CustomerDeclaration.ConfirmAppointmentSheet.atHome' | translate
              }}</span>
            </mat-radio-button>
          </mat-radio-group>
          <sc-error
            id="errorLocation"
            *ngIf="isErrorLocationChoice()"
            [displayCondition]="true"
            [error]="error"
          ></sc-error>
        </div>

        <div
          *ngIf="showReplacementVehicleChoice()"
          class="replacement-vehicle-choice"
        >
          <h2 id="replacementVehicleQuestion" class="sc-design-text-2 subtitle">
            {{
              'CustomerDeclaration.ConfirmAppointmentSheet.replacementVehicleQuestion'
                | translate
            }}
          </h2>

          <mat-radio-group
            [(ngModel)]="replacementVehicleChoice"
            [attr.aria-describedby]="
              'replacementVehicleQuestion' +
              (isErrorReplacementVehicleChoice()
                ? ' errorReplacementVehicle'
                : '')
            "
          >
            <mat-radio-button [value]="true">
              <span class="sc-design-text-3">{{
                'CustomerDeclaration.ConfirmAppointmentSheet.replacementVehicleYes'
                  | translate
              }}</span>
            </mat-radio-button>
            <mat-radio-button [value]="false">
              <span class="sc-design-text-3">{{
                'CustomerDeclaration.ConfirmAppointmentSheet.replacementVehicleNo'
                  | translate
              }}</span>
            </mat-radio-button>
          </mat-radio-group>
          <sc-error
            id="errorReplacementVehicle"
            *ngIf="isErrorReplacementVehicleChoice()"
            [displayCondition]="true"
            [error]="error"
          ></sc-error>
        </div>

        <div *ngIf="displayReplacementVehicleAlert()" class="apmt-alert-box">
          <span class="apmt-alert-box-icon">
            <sc-icon name="warning_circle"></sc-icon>
          </span>
          <span class="sc-design-text-3">{{
            'CustomerDeclaration.ConfirmAppointmentSheet.replacementVehicleAlert'
              | translate: { clientAppId: clientAppId }
          }}</span>
        </div>

        <div *ngIf="displayAdasAlert() | async" class="apmt-alert-box title">
          <div class="apmt-alert-box-icon">
            <sc-icon name="warning_circle"></sc-icon>
            <span class="sc-design-text-3-bold">{{
              'CustomerDeclaration.ConfirmAppointmentSheet.adasAlertTitle'
                | translate
            }}</span>
          </div>
          <div class="sc-design-text-3">
            {{
              'CustomerDeclaration.ConfirmAppointmentSheet.adasAlert'
                | translate
            }}
          </div>
        </div>

        <div class="info-text">
          <p
            class="sc-design-text-4"
            [innerHTML]="
              ((displayAdasDurationRange() | async)
                ? 'CustomerDeclaration.ConfirmAppointmentSheet.immoAdasInfoText'
                : 'CustomerDeclaration.ConfirmAppointmentSheet.immoInfoText'
              )
                | translate
                  : {
                      duration: getImmoDurationString(),
                      durationAdas: getAdasInterventionDurationString()
                    }
            "
            *ngIf="
              !appointment?.outsideIntervention ||
              !featureFlipManager.isBrandFeatureActivated(
                this.fitter?.brandId,
                'display-PAD-as-half-day'
              )
            "
          ></p>
        </div>

        <div
          *ngIf="displayCustomerContactConfirmation()"
          class="info-selected-appointment-container"
          [formGroup]="form"
        >
          <h3 class="sc-design-text-2">
            {{
              'CustomerDeclaration.ConfirmAppointmentSheet.headerCustomerInformations'
                | translate
            }}
          </h3>
          <sc-input-text
            id="customerEmailInput"
            formControlName="email"
            autocomplete="email"
            [hint]="
              'GlobalApplicativeConstants.accessibility.labels.emailExemple'
                | translate
            "
            [label]="
              'CustomerDeclaration.ConfirmAppointmentSheet.emailInput'
                | translate
            "
            errorPattern="emailPattern"
          ></sc-input-text>
          <sc-phone-input
            id="customerPhoneInput"
            formControlName="phoneNumber"
            [label]="
              'CustomerDeclaration.ConfirmAppointmentSheet.phoneInput'
                | translate
            "
            scKeyPress="phoneNumber"
            [displayCountrySelection]="true"
            [setCountry]="form.get('phoneCountry').value"
            (changeCountry)="form.get('phoneCountry').setValue($event)"
            [required]="
              !featureFlipManager.isFeatureActivated(
                'b2b-allow-only-one-contact-channel-phone-or-email'
              )
            "
          ></sc-phone-input>
        </div>

        <div mat-dialog-actions class="buttons">
          <sc-button
            type="button"
            class="sheet-action-btn btn-validate"
            [buttonType]="canValidateAppointment() ? 'primary' : 'grey'"
            [attr.data-testid]="'validateAppointmentButton'"
            (click)="onValidateAppointment()"
            >{{ getBtnValidate() | translate }}</sc-button
          >
          <sc-button
            type="button"
            class="sheet-action-btn btn-cancel"
            buttonType="tertiary"
            (click)="onCancelAppointment()"
            >{{ getBtnCancel() | translate }}</sc-button
          >
        </div>
      </div>
    </div>
  `,
})
export class AppointmentSelectionSheetComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() onlyReplacementVehicle = false;
  @Input() displayReplacementVehicleSelection = false;
  @Input() location: LocationDataGeocoded;
  @Input() appointment: Appointment;
  @Input() customer: Customer;
  @Input() fitter: Fitter;

  @Output() cancelEvent = new EventEmitter<void>();
  @Output() validationEvent =
    new EventEmitter<AppointmentSheetValidationData>();

  form: FormGroup;
  error: ErrorMessage = {
    title: this.translatorService.getInstantTranslation(
      'MatErrors.requiredErrorTitle'
    ),
    message: this.translatorService.getInstantTranslation(
      'MatErrors.requiredErrorMessage',
      {
        fieldName: this.translatorService.getInstantTranslation(
          'PlanningFormAppointmentIntervention.serviceTypeSelection'
        ),
      }
    ),
  };
  locationChoice: 'center' | 'outside' = null;
  replacementVehicleChoice: boolean = null;

  modifiedLocation: LocationDataGeocoded;

  showUnknownAddressForm: boolean;

  displayFitterLogo = false;
  useDefaultFitterLogo = false;
  fitterLogoUrl = '';
  fitterLogoDefaultUrl = '';
  fitterLogoAlt$: Observable<string> = of('');

  selectedAddressStr: string;
  googleAutoCompleteResponse: LocationData[] = [];
  isAddressInputError: boolean;

  isFormTouched = false;
  operation: string;
  private readonly addressSuggestionsSubject$ = new Subject<string>();

  constructor(
    private readonly translatorService: TranslatorService,
    private readonly sandboxService: SandboxService,
    private readonly assetsService: AssetsService,
    public timezoneService: TimezoneService,
    public fittersSB: FittersSandboxService,
    private readonly changeDetector: ChangeDetectorRef,
    public featureFlipManager: FeatureFlippingManagerService,
    private readonly formBuilder: FormBuilder,
    public errorMessagesService: ErrorMessagesService,
    public dossierSB: DossierSandboxService,
    public configurationService: ConfigurationService,
    @Inject(CLIENT_APP_ID) public clientAppId: ClientAppId
  ) {
    this.showUnknownAddressForm = false;

    this.addressSuggestionsSubject$
      .pipe(
        debounceTime(500),
        filter((value) => value.length >= 3)
      )
      .subscribe((newAddress: string) => {
        this.sandboxService
          .getPlacesFromAddress(
            // We don't use the postal code in the search because it can vary inside the same city and
            // restrain the search every time we click on a suggestion
            `${this.modifiedLocation?.addressElements.city} ${newAddress}`
          )
          .pipe(
            take(1),
            map((places) =>
              places.filter(
                (place) =>
                  place.address.includes(
                    this.modifiedLocation?.addressElements.city
                  ) && place.isCompleteAddress
              )
            ),
            map((places) => this.addAddressNotFound(places))
          )
          .subscribe({
            next: (places) => {
              this.googleAutoCompleteResponse = places;
              this.changeDetector.markForCheck();
            },
          });
      });
  }

  ngOnInit(): void {
    this.dossierSB.dossier$
      .pipe(
        filter((dossier) => !!dossier?.claim?.glass),
        take(1)
      )
      .subscribe((dossier) => {
        this.operation = getOperationType(dossier.claim.glass)?.toLowerCase();
      });

    this.initFitterLogo();

    if (
      !this.featureFlipManager.isBrandFeatureActivated(
        this.fitter.brandId,
        'fitter-in-mobility-service'
      )
    ) {
      this.locationChoice = 'center';
    }
    if (this.onlyReplacementVehicle) {
      this.replacementVehicleChoice = true;
    } else if (
      this.isAppointmentRequest() &&
      this.featureFlipManager.sendRequestNotificationOnlyToCustomer(
        this.fitter?.brandId
      )
    ) {
      this.replacementVehicleChoice = false;
    }
    this.modifiedLocation = {
      ...this.location,
      addressElements: { ...this.location.addressElements },
      geoCoordinates: { ...this.location.geoCoordinates },
    };
    this.selectedAddressStr = this.modifiedLocation?.addressElements.streetName;
    this.googleAutoCompleteResponse = [];
    if (this.selectedAddressStr) {
      this.onUpdateAddressSuggestions(this.selectedAddressStr);
    }
    this.form = this.formBuilder.group(
      {
        email: [
          this.customer?.email,
          [
            this.featureFlipManager.isFeatureActivated(
              'b2b-allow-only-one-contact-channel-phone-or-email'
            )
              ? Validators.nullValidator
              : Validators.required,
            Validators.pattern(emailPattern),
            Validators.maxLength(100),
          ],
        ],
        phoneNumber: [this.customer?.phoneNumber?.number],
        phoneCountry: [this.customer?.phoneNumber?.countryCode],
      },
      this.featureFlipManager.isFeatureActivated(
        'b2b-allow-only-one-contact-channel-phone-or-email'
      )
        ? {
            validator: CustomValidators.atLeastOneOfTwo('phoneNumber', 'email'),
          }
        : null
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fitter) {
      this.initFitterLogo();
    }
  }

  private initFitterLogo(): void {
    const featureEnabled = this.featureFlipManager.isFeatureActivated(
      'b2c-display-fitter-logo-on-appointment-selection-sheet'
    );
    const fitterLogo = this.configurationService.getFitterBrandInfosByBrandId(
      this.fitter?.brandId
    )?.logo;

    this.displayFitterLogo = featureEnabled && !!fitterLogo;

    console.info('[AppointmentSelectionSelectionSheet][FitterLogo] init', {
      configId: this.configurationService.getConfig()?.id,
      brandId: this.fitter?.brandId,
      featureEnabled,
      fitterLogo,
      displayFitterLogo: this.displayFitterLogo,
    });

    if (!this.displayFitterLogo) {
      console.info('[AppointmentSelectionSheet][FitterLogo] disabled', {
        reason: featureEnabled ? 'missing-logo' : 'feature-disabled',
        brandId: this.fitter?.brandId,
      });
      return;
    }

    this.useDefaultFitterLogo = false;
    this.fitterLogoUrl = this.assetsService.getBrandLogoPath(fitterLogo, true);
    this.fitterLogoDefaultUrl = '';
    this.fitterLogoAlt$ = this.assetsService.getLogoAlt(
      fitterLogo,
      'fitterBrandIdLogo'
    );

    console.info('[AppointmentSelectionSheet][FitterLogo] resolved', {
      brandId: this.fitter?.brandId,
      fitterLogo,
      path: this.fitterLogoUrl,
    });
  }

  getDisplayedFitterLogoPath(): string {
    return this.useDefaultFitterLogo
      ? this.fitterLogoDefaultUrl
      : this.fitterLogoUrl;
  }

  onFitterLogoError(): void {
    console.warn('[AppointmentSelectionSheet][FitterLogo] load error', {
      configId: this.configurationService.getConfig()?.id,
      brandId: this.fitter?.brandId,
      path: this.getDisplayedFitterLogoPath(),
    });

    this.displayFitterLogo = false;

    this.changeDetector.markForCheck();
  }

  getTypeAppointmentClass(): string {
    return this.isOutsideIntervention() ? 'at-home' : 'in-center';
  }

  getImmoDurationString(): string {
    const usedDuration = resolveInterventionDuration(
      this.configurationService.getConfig(),
      this.operation,
      this.appointment?.immobilisationDuration
    );

    const durationObj = getTranslatableDuration(usedDuration);
    return this.translatorService.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  getAdasInterventionDurationString(): string {
    const usedDuration = resolveInterventionDuration(
      this.configurationService.getConfig(),
      'replaceAdas'
    );
    const durationObj = getTranslatableDuration(usedDuration);
    return this.translatorService.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  displayAdasDurationRange() {
    return this.dossierSB.dossier$.pipe(
      filter((dossier) => !!dossier),
      take(1),
      map(
        (dossier) =>
          this.featureFlipManager.isFeatureActivated(
            'display-adas-duration-range'
          ) &&
          dossier?.claim?.glass?.glass === 'windshield' &&
          getOperationType(dossier.claim.glass)?.toLowerCase() === 'replace'
      )
    );
  }

  getOnlyCustomerKey() {
    const brandId = this.fitter?.brandId;
    return this.featureFlipManager.sendRequestNotificationOnlyToCustomer(
      brandId
    )
      ? 'onlyCustomer'
      : 'all';
  }

  getRequestAppointmentDescription() {
    return `CustomerDeclaration.ConfirmAppointmentSheet.requestAppointment.${
      this.clientAppId
    }.${this.getOnlyCustomerKey()}.description`;
  }

  getBtnValidate() {
    if (this.isAppointmentRequest())
      return `CustomerDeclaration.ConfirmAppointmentSheet.requestAppointment.${
        this.clientAppId
      }.${this.getOnlyCustomerKey()}.btnValidate`;
    else
      return 'CustomerDeclaration.ConfirmAppointmentSheet.btnValidateAppointment';
  }

  getBtnCancel() {
    if (this.isAppointmentRequest())
      return 'CustomerDeclaration.ConfirmAppointmentSheet.requestAppointment.btnChange';
    else
      return 'CustomerDeclaration.ConfirmAppointmentSheet.btnChangeAppointment';
  }

  isAppointmentRequest() {
    return !this.appointment;
  }

  showLocationChoice() {
    return (
      this.isAppointmentRequest() &&
      this.featureFlipManager.isBrandFeatureActivated(
        this.fitter.brandId,
        'fitter-in-mobility-service'
      )
    );
  }

  showReplacementVehicleChoice(): boolean {
    return (
      this.featureFlipManager.isBrandFeatureActivated(
        this.fitter.brandId,
        'can-declare-replacement-vehicle-fitter'
      ) &&
      ((this.isAppointmentRequest() && this.locationChoice !== 'outside') ||
        (this.appointment?.replacementVehicle &&
          this.displayReplacementVehicleSelection))
    );
  }

  isOutsideIntervention(): boolean {
    return (
      (this.isAppointmentRequest() && this.locationChoice === 'outside') ||
      this.appointment?.outsideIntervention
    );
  }

  showAddressCompletionForm(): boolean {
    return this.isOutsideIntervention() && !this.location.isCompleteAddress;
  }

  getAutocompleteHintText(): string {
    return this.isOutsideIntervention() && !this.canValidateAppointment()
      ? this.translatorService.getInstantTranslation(
          'CustomerDeclaration.ConfirmAppointmentSheet.addressHint'
        )
      : '';
  }

  getCityLabel() {
    return !this.showUnknownAddressForm
      ? this.modifiedLocation?.addressElements?.postalCode
        ? this.modifiedLocation?.addressElements?.postalCode +
          ' ' +
          this.modifiedLocation?.addressElements?.city
        : this.modifiedLocation?.addressElements?.city
      : this.modifiedLocation?.addressElements?.city;
  }

  cityNameIsError() {
    return !this.modifiedLocation?.addressElements.city;
  }

  streetNameIsError() {
    return !this.modifiedLocation?.addressElements.streetName;
  }

  postalCodeIsError() {
    return (
      !this.modifiedLocation?.addressElements?.postalCode ||
      this.modifiedLocation?.addressElements?.postalCode?.length < 4
    );
  }

  canValidateAppointment(): boolean {
    const isLocationChosen =
      !this.isAppointmentRequest() || !!this.locationChoice;

    const canDisplayInCenter =
      !this.showAddressCompletionForm() &&
      (!this.showReplacementVehicleChoice() ||
        this.replacementVehicleChoice === true ||
        this.replacementVehicleChoice === false);

    const canDisplayAtHome =
      this.showAddressCompletionForm() &&
      !this.streetNameIsError() &&
      !this.cityNameIsError() &&
      !this.postalCodeIsError();

    return (
      (canDisplayInCenter || canDisplayAtHome) &&
      isLocationChosen &&
      (this.form.valid || !this.displayCustomerContactConfirmation())
    );
  }

  isErrorReplacementVehicleChoice(): boolean {
    return (
      this.isFormTouched &&
      this.showReplacementVehicleChoice() &&
      this.replacementVehicleChoice === null
    );
  }

  isErrorLocationChoice(): boolean {
    return (
      this.isFormTouched &&
      this.showLocationChoice() &&
      this.locationChoice === null
    );
  }

  addAddressNotFound(places: LocationData[] = []): LocationData[] {
    return [
      ...places,
      {
        address: this.translatorService.getInstantTranslation(
          'GlobalErrors.addressNotFound'
        ),
        placeId: null,
        isKnownAddress: false,
        isCompleteAddress: null,
        locationProvider: null,
      },
    ];
  }

  setSelectedAddress(newLocation: LocationData | LocationDataGeocoded) {
    if ('addressElements' in newLocation) {
      this.selectedAddressStr = [
        newLocation.addressElements.streetNumber,
        newLocation.addressElements.streetName,
      ].join(' ');
    } else {
      this.selectedAddressStr = newLocation.address;
    }
    this.isAddressInputError = !this.canValidateAppointment();
  }

  onAddressChangeValue(event: Event) {
    const value = (event as HTMLElementEvent<HTMLInputElement>)?.target?.value;

    this.isAddressInputError = !value || this.isAddressInputError;
  }

  displayReplacementVehicleAlert() {
    return (
      (this.isAppointmentRequest() ||
        this.featureFlipManager.isFeatureActivated(
          'force-display-request-appointment-alerts'
        )) &&
      !!this.replacementVehicleChoice
    );
  }

  displayAdasAlert() {
    return this.dossierSB.dossier$.pipe(
      filter((dossier) => !!dossier),
      take(1),
      map(
        (dossier) =>
          this.featureFlipManager.isFeatureActivated(
            'can-display-adas-alert'
          ) &&
          dossier?.claim?.calibration === 'true' &&
          dossier?.claim?.glass?.glass === 'windshield' &&
          !!this.appointment?.outsideIntervention &&
          this.clientAppId === 'customer'
      )
    );
  }

  displayCustomerContactConfirmation() {
    return (
      this.featureFlipManager.isFeatureActivated(
        'b2b-display-customer-info-confirmation-on-validation'
      ) && this.clientAppId === 'insurer'
    );
  }

  onSelectAddress(locationData: LocationData) {
    if (!locationData.placeId && this.modifiedLocation) {
      this.showUnknownAddressForm = true;
      this.modifiedLocation.placeId = null;
      this.modifiedLocation.isKnownAddress = false;
      this.modifiedLocation.isCompleteAddress = false;
      this.changeDetector.detectChanges();
    } else if (locationData.placeId && this.modifiedLocation?.placeId) {
      // The condition is needed because this function is triggered one more time
      // after selecting unknown address with the previous (and possibly valid) address
      this.sandboxService
        .getGeoCodeFromPlaceId(locationData.placeId)
        .subscribe({
          next: (location) => {
            this.modifiedLocation = location;
            this.setSelectedAddress(this.modifiedLocation);
            this.changeDetector.detectChanges();
          },
          error: () => {
            this.modifiedLocation = {
              ...this.modifiedLocation,
              ...locationData,
            };
            this.setSelectedAddress(locationData);
            this.changeDetector.detectChanges();
          },
        });
    }
  }

  onUpdateAddressSuggestions(newAddress: string) {
    this.addressSuggestionsSubject$.next(newAddress);
  }

  ngOnDestroy(): void {
    this.addressSuggestionsSubject$.complete();
  }

  onChangeStreetNumber($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetNumber = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateModifiedLocationAdressAttribute();
    }
  }

  onChangeStreetSuffix($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetSuffix = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateModifiedLocationAdressAttribute();
    }
  }

  onChangeStreetName($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.streetName = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
      this.updateModifiedLocationAdressAttribute();
    }
  }

  isCustomerClientAppId() {
    return this.clientAppId === ('customer' as ClientAppId);
  }

  updateModifiedLocationAdressAttribute() {
    if (this.showUnknownAddressForm && this.showAddressCompletionForm()) {
      let address = this.modifiedLocation?.addressElements?.streetNumber ?? '';
      address += this.modifiedLocation?.addressElements?.streetSuffix
        ? ' ' + this.modifiedLocation?.addressElements?.streetSuffix
        : '';
      address += this.modifiedLocation?.addressElements?.streetName
        ? ' ' + this.modifiedLocation?.addressElements?.streetName
        : '';

      address += address?.length
        ? ', ' +
          this.modifiedLocation?.addressElements?.postalCode +
          ' ' +
          this.modifiedLocation?.addressElements?.city
        : +this.modifiedLocation?.addressElements?.postalCode +
          ' ' +
          this.modifiedLocation?.addressElements?.city;

      this.modifiedLocation.address = address;
    }
  }

  onChangePostalCode($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.postalCode = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
    }
  }

  onChangeCity($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.city = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
    }
  }

  onCancelAppointment() {
    this.cancelEvent.emit();
  }

  onValidateAppointment() {
    this.isFormTouched = true;
    if (this.canValidateAppointment()) {
      this.modifiedLocation.isCompleteAddress = true;
      const internationalCode = getMatchingPhonePattern(
        this.form.get('phoneNumber').value,
        this.form.get('phoneCountry').value as keyof typeof phonePatterns
      )?.code;
      this.validationEvent.emit({
        replacementVehicle: !!this.replacementVehicleChoice,
        locationChoice: this.locationChoice,
        location:
          this.locationChoice === 'center'
            ? AppointmentUtils.getFitterLocation(this.fitter)
            : this.modifiedLocation,
        customer: {
          ...this.customer,
          email: this.form.get('email').value,
          phoneNumber: {
            number: this.dossierSB.formatCustomerPhoneNumber(
              this.form.get('phoneNumber').value
            ),
            countryCode: this.form.get('phoneCountry').value,
            internationalCode: internationalCode,
          },
        },
      });
    }
  }
}
